import { BookingRequest } from '../../../APIDocs/apiTypes';
import { JbpGeneralState } from '@/reducers/jbpGeneralSlice';

export const useBookingPayload = (state: JbpGeneralState): BookingRequest | undefined => {
  const { site, jobDetails, offer } = state;
  if (!offer || !site || !jobDetails) return undefined;

  const isWindowJobBooking = offer.windowBookingStartDate != undefined;

  if (!isWindowJobBooking && !offer.selectedOffer) {
    throw new Error('Selected offer is not set in state for non window booking');
  }

  const generateJobComments = (): string => {
    // when rebooking we don't need to format the comments a second time
    if (jobDetails.relatedBookingId) return jobDetails.jobComments;
    if (jobDetails.isWarrantJob) {
      return [
        `Warrant Officer Name: ${jobDetails.warrantOfficerName} \n`,
        `Warrant Officer Contact Number: ${jobDetails.warrantOfficerContactNumber} \n`,
        jobDetails.whatThreeWords ? `What 3 Words: ${jobDetails.whatThreeWords} \n` : '',
        jobDetails.jobComments
      ].join('');
    }
    return [
      jobDetails.liftOnSite ? 'Lift on site \n' : '',
      jobDetails.accessInstructions ? `Access Instructions - ${jobDetails.accessInstructions} \n` : '',
      jobDetails.whatThreeWords ? `What3Words: ${jobDetails.whatThreeWords} \n` : '',
      jobDetails.parkingInfo ? `Parking Info: ${jobDetails.parkingInfo.join(', ')} \n` : '',
      jobDetails.customerAgreesToPowerDown ? 'Powerdown Agreed: Yes \n' : '',
      !jobDetails.siteInductionRequired ? 'Induction: No \n' : '',
      jobDetails.extraSkills ? `Extra Skills: ${jobDetails.extraSkills.join(', ')} \n` : '',
      jobDetails.jobComments
    ].join('');
  };

  const payload: BookingRequest = {
    BookingType: isWindowJobBooking ? 'windowBooking' : 'booking',
    Forced: offer.forceOfferSelection ? 'Yes' : 'No',
    OfferToken: isWindowJobBooking ? '' : offer.selectedOffer!.offerToken,
    StartTime: isWindowJobBooking ? offer.windowBookingStartDate! : offer.selectedOffer!.startTime,
    SiteType: site.updatedSiteType ? site.updatedSiteType : site.siteType,
    JobType: jobDetails.jobTypeCode,
    RamsRequired: jobDetails.ramsRequired ?? false,
    RamsEmail: jobDetails.ramsEmail,
    SiteDetails: {
      ParkingAvailable: true
    },
    ExtraSkills: jobDetails.extraSkills,
    RelatedBookingId: jobDetails.relatedBookingId,
    BookingReasonId: site.bookingReason,
    SiteAddress: {
      AddressName: jobDetails.siteAddress.addressName,
      AddressLine1: jobDetails.siteAddress.addressLine1,
      AddressLine2: jobDetails.siteAddress.addressLine2,
      AddressLine3: jobDetails.siteAddress.addressLine3,
      Town: jobDetails.siteAddress.town,
      County: jobDetails.siteAddress.county,
      PostCode: jobDetails.siteAddress.postCode
    },
    SiteContactDetails: {
      Title: jobDetails.siteContactDetails.title,
      ContactFirstName: jobDetails.siteContactDetails.contactFirstName,
      ContactSurname: jobDetails.siteContactDetails.contactSurname,
      EmailAddress: jobDetails.siteContactDetails.emailAddress,
      ContactNumber: jobDetails.siteContactDetails.contactNumber,
      MobileNumber: jobDetails.siteContactDetails.secondaryContactNumber
    },
    NotificationPreferences: {
      SmsReminderRequested: !jobDetails.isWarrantJob ? jobDetails.smsReminderRequired : false,
      SmsConfirmationRequested: !jobDetails.isWarrantJob ? jobDetails.smsConfirmationRequired : false,
      NotificationSms: !jobDetails.isWarrantJob && jobDetails.smsConfirmationRequired ? jobDetails.smsConfirmationNumber : undefined
    },
    JobComments: generateJobComments()
  };

  if (!isWindowJobBooking) {
    payload.EndTime = offer.selectedOffer!.endTime;
    payload.ScheduledDate = offer.selectedOffer!.scheduledDate;
    payload.SpJobCode = offer.selectedOffer!.spJobCode;
  }

  return payload;
};
